import { useMutation } from 'react-query'
import Cookies from 'cookies-js'

import {
  getLiveId,
  getPresenterToken,
  getRole,
} from '../../global/blocs/roomInfo'
import { getLiveToken } from '../../global/blocs/useConnectInfo'
import {
  addComment,
  addCommentReaction,
  cloneComment,
  deleteComments,
  promoteComment,
  removeCommentReaction,
} from '../api/commentApi'

import { type ChatId } from './chatVisibility'
import {
  addMessage,
  addReaction,
  deleteMessage,
  pinMessage,
  removeReaction,
  updateMessageId,
} from './useChat'

interface PostChatMessageParams {
  message: string
  replyId?: number
}

const usePostChatMessageMutation = (type: ChatId) => {
  return useMutation({
    mutationFn: async ({ message, replyId }: PostChatMessageParams) => {
      const name =
        getRole() === 'audience' ? window.localStorage?.comment_name : undefined

      return addComment({
        object_id: getLiveId(),
        object_type: 'live',
        comment_type: type,
        object_token: await getLiveToken(),
        reply_to_comment_id: replyId,
        content: message,
        uuid: getRole() === 'audience' ? Cookies.get('uuid') : undefined,
        presenter_token: await getPresenterToken(),
        name,
      })
    },
    onMutate: async ({ message, replyId }: PostChatMessageParams) => {
      const tempId = await addMessage(type, message, replyId)
      return { tempId }
    },
    onSuccess: ({ data: { comment_id: newId } }, _, context) => {
      const tempId = context?.tempId
      if (tempId && newId) updateMessageId(type, tempId, newId)
    },
    onError: (_: Error, __, context) => {
      const tempId = context?.tempId
      if (tempId) deleteMessage(tempId)
    },
  })
}

const useAddCommentReactionMutation = (commentId: number) => {
  return useMutation({
    mutationFn: async (emoji: string) => {
      return addCommentReaction({
        object_id: getLiveId(),
        object_token: await getLiveToken(),
        object_type: 'live',
        comment_id: commentId,
        reaction_emoji: emoji,
        uuid: getRole() === 'audience' ? Cookies.get('uuid') : undefined,
        presenter_token: await getPresenterToken(),
      })
    },
    onMutate: (emoji: string) => {
      addReaction(commentId, emoji)
      return { emoji }
    },
    onError: (_: Error, __, context) => {
      if (!context) return
      removeReaction(commentId, context.emoji)
    },
  })
}

const useRemoveCommentReactionMutation = (commentId: number) => {
  return useMutation({
    mutationFn: async (emoji: string) => {
      return removeCommentReaction({
        object_id: getLiveId(),
        object_token: await getLiveToken(),
        object_type: 'live',
        comment_id: commentId,
        reaction_emoji: emoji,
        uuid: getRole() === 'audience' ? Cookies.get('uuid') : undefined,
        presenter_token: await getPresenterToken(),
      })
    },
    onMutate: (emoji: string) => {
      removeReaction(commentId, emoji)
      return { emoji }
    },
    onError: (_: Error, __, context) => {
      if (!context) return
      addReaction(commentId, context.emoji)
    },
  })
}

const usePinCommentMutation = (commentId: number) => {
  return useMutation({
    mutationFn: (pin: boolean) => {
      return promoteComment({
        comment_id: commentId,
        promoted_p: pin,
      })
    },
    onMutate: async (pinned: boolean) => {
      pinMessage(commentId, pinned)
      return { pinned }
    },
    onError: (_: Error, __, context) => {
      if (!context) return
      pinMessage(commentId, !context.pinned)
    },
  })
}

const useDeleteCommentMutation = (commentId: number) => {
  return useMutation({
    mutationFn: () => {
      return deleteComments({
        comment_id: commentId,
      })
    },
    onSuccess: () => {
      deleteMessage(commentId)
    },
  })
}

const useMakeCommentIntoQuestionMutation = (commentId: number) => {
  return useMutation({
    mutationFn: () => {
      return cloneComment({
        comment_id: commentId,
        clone_comment_type: 'question',
      })
    },
  })
}

export {
  usePostChatMessageMutation,
  useAddCommentReactionMutation,
  usePinCommentMutation,
  useDeleteCommentMutation,
  useMakeCommentIntoQuestionMutation,
  useRemoveCommentReactionMutation,
}
